import React from "react";
import { Link } from "react-router-dom";
import ValuationForm from "@/components/ValuationForm";
import PriceEstimator from "@/components/PriceEstimator";
import SEO from "@/components/SEO";
import { CheckCircle2, TrendingUp, Camera, Users, FileText, Handshake } from "lucide-react";

export default function VenderPage() {
  const steps = [
    { icon: TrendingUp, title: "1. Avaliação realista", desc: "Análise do mercado local e relatório claro do valor sugerido." },
    { icon: Camera, title: "2. Preparação e fotografia", desc: "Home staging leve e fotografias profissionais que valorizam o imóvel." },
    { icon: Users, title: "3. Marketing e divulgação", desc: "Portais premium, redes sociais e base de compradores qualificados." },
    { icon: FileText, title: "4. Visitas selecionadas", desc: "Filtro prévio de compradores para não desperdiçar o seu tempo." },
    { icon: Handshake, title: "5. Negociação humana", desc: "Consigo o melhor preço, dentro do prazo, sem pressão desnecessária." },
    { icon: CheckCircle2, title: "6. Escritura e pós-venda", desc: "Acompanhamento até à escritura e depois dela." },
  ];

  return (
    <div data-testid="page-vender">
      <SEO
        title="Vender casa no Porto — Avaliação gratuita | Zélia Silva"
        description="Vender casa no Porto, Gondomar, Maia, Ermesinde ou Valongo? Peça uma avaliação gratuita à Zélia Silva. Estratégia, marketing e negociação por uma consultora com 10+ anos."
        canonical="https://casanoporto.com/vender-casa-no-porto"
      />
      <section className="container-cnp pt-14 lg:pt-20 pb-12">
        <div className="max-w-3xl">
          <span className="tag-chip">Para vendedores · Porto e Grande Porto</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]">
            Vender casa no Porto,<br/><span className="italic text-[#103C4A]">com estratégia e serenidade.</span>
          </h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed">
            Uma venda bem feita começa muito antes do anúncio. Aqui explico como trabalho — para que possa
            decidir com toda a informação, sem compromisso.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#avaliacao" data-testid="vender-cta-avaliacao" className="btn-primary">Pedir avaliação gratuita</a>
            <Link to="/contacto" data-testid="vender-cta-conversa" className="btn-secondary">Marcar uma reunião</Link>
          </div>
        </div>
      </section>

      <section className="container-cnp py-12 lg:py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { n: "0€", l: "custo para começar" },
            { n: "48h", l: "resposta com relatório" },
            { n: "100%", l: "acompanhamento até à escritura" },
          ].map((s, i) => (
            <div key={i} data-testid={`vender-stat-${i}`} className="card-soft p-8">
              <div className="font-display text-4xl text-[#103C4A]">{s.n}</div>
              <div className="mt-2 text-sm uppercase tracking-widest text-stone-500">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-cnp py-12 lg:py-16" id="comparador">
        <div className="max-w-2xl mb-8">
          <span className="tag-chip">Comparador em segundos</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900 tracking-[-0.02em]">Quanto vale a minha casa, aproximadamente?</h2>
          <p className="mt-3 text-stone-600">Uma estimativa rápida com base no €/m² actual da sua zona e tipologia. Para o valor exacto, peça uma avaliação em baixo.</p>
        </div>
        <PriceEstimator />
      </section>

      <section className="bg-white border-y border-stone-200/70">
        <div className="container-cnp py-16 lg:py-20">
          <div className="max-w-2xl">
            <span className="tag-chip">Método</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Um processo transparente, do primeiro dia à escritura.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} data-testid={`vender-step-${i}`} className="card-soft p-7">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]"><s.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 text-lg text-stone-900">{s.title}</h3>
                <p className="mt-2 text-sm text-stone-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="avaliacao" className="container-cnp py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="tag-chip">Avaliação gratuita</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Quanto vale a sua casa hoje?</h2>
          <p className="mt-4 text-stone-600">
            Preencha os dados abaixo e envio-lhe uma proposta de valor realista, sem compromisso e sem pressão para avançar.
          </p>
          <ul className="mt-6 space-y-3 text-stone-700">
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-[#103C4A]" /> Análise do mercado local</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-[#103C4A]" /> Comparativos recentes</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-[#103C4A]" /> Plano de marketing incluído</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-[#103C4A]" /> 100% confidencial</li>
          </ul>
        </div>
        <div className="lg:col-span-7 card-soft p-8">
          <ValuationForm />
        </div>
      </section>
    </div>
  );
}
